import React, { useEffect, useState } from "react";
import { Card, Accordion, Row, Col } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { ReactComponent as SortIcon } from "assets/images/icons/SortDown.svg";
import Button from "components/atoms/Button/Button";
import Textarea from "components/atoms/Textarea/Textarea";
import Alert from "components/molecules/Alert/Alert";
import { FormStepTypes } from "constants/";
import Organisational from "./organisational";
import Schedule from "./Schedule";
import PackingList from "./PackingList";
import FreightMode from "./FreightMode";
import Attachment from "./Attachment";
import Service from "./Service";
import Shipment from "./Shipment";
import Quotation from "./Quotation";

type FormConfirmationProps = {
  onSubmitClick: any;
  state: any;
  isQuoteRequest: boolean;
  isSubmitting: boolean;
  offPlatformOptionalFields?: boolean;
  isNewShipment?: boolean;
};

function FormConfirmation(props: FormConfirmationProps) {
  const { onSubmitClick, state, isQuoteRequest, isSubmitting, offPlatformOptionalFields } = props;
  const methods = useFormContext();
  const { getValues } = methods;

  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const currentActiveStep = searchParams.get("active_step");
  const [isShipment, setIsShipmet] = useState<boolean>(false);
  const [editError, setEditError] = useState<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const quotesAvailable = searchParams.get("quotesAvailable") === "true";
    setEditError(quotesAvailable);
  }, []);

  useEffect(() => {
    if (location?.pathname === "/tracker") {
      setIsShipmet(true);
    }
  }, [location]);

  const handleSubmitClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    onSubmitClick();
  };

  const renderSubmitButton = () => (
    <Button
      className="rounded-pill mr-4"
      onClick={handleSubmitClick}
      label={intl.formatMessage({
        id: "general.submit",
        defaultMessage: "Submit",
      })}
      isLoading={isSubmitting}
    />
  );

  return (
    <Card className="mb-3 form-container confirmation-container">
      <Accordion.Toggle as={Card.Header} className="accordion-header" eventKey={FormStepTypes.CONFIRMATION}>
        <div className="w-100">
          <div className="d-flex align-items justify-content-between w-100">
            <h5 className="d-flex align-items-center">
              <FormattedMessage id="new-request.quote-confirmation.schedule.confirmation" defaultMessage="Confirmation" />
            </h5>
            {editError && (
              <div style={{ textTransform: "none" }} className="ml-3 mr-3">
                <Alert variant="warning" className="text-black">
                  <div>
                    <div className="font-weight-bold mb-1">
                      <FormattedMessage
                        id="new-request.form.editBookingError"
                        defaultMessage="You will not be able to book submitted quotes after quote request changes. {br}"
                        values={{ br: <br /> }}
                      />
                    </div>

                    <FormattedMessage
                      id="new-request.form.editBookingSubtext"
                      defaultMessage="The providers will be asked to re-confirm their quotes to make sure costs are relevant to latest quote request updates."
                    />
                  </div>
                </Alert>
              </div>
            )}

            <div className="d-flex align-items-center">
              {currentActiveStep !== FormStepTypes.CONFIRMATION && renderSubmitButton()}

              <SortIcon
                fill="#000"
                width={8}
                height={7}
                transform={`rotate(${currentActiveStep === FormStepTypes.CONFIRMATION ? 180 : 0})`}
              />
            </div>
          </div>
        </div>
      </Accordion.Toggle>
      <Accordion.Collapse eventKey={FormStepTypes.CONFIRMATION}>
        {isShipment && !isQuoteRequest ? (
          <Card.Body>
            <Row>
              <Col>
                <div className="mb-4 font-weight-bold">
                  <FormattedMessage id="components.molecules.navigation.shipmentDetails" defaultMessage="Shipment details" />
                </div>
                <Shipment state={state} />
              </Col>
              <Col>
                <div className="mb-4 font-weight-bold">
                  <FormattedMessage id="quote-request-detail.schedule" defaultMessage="Schedule" />
                </div>
                <Schedule offPlatformOptionalFields={offPlatformOptionalFields} />
              </Col>
              <Col>
                {offPlatformOptionalFields && (
                  <>
                    <div className="mb-4 font-weight-bold">
                      <FormattedMessage id="quote.listing.quotation" defaultMessage="Quotation" />
                    </div>
                    <Quotation />
                  </>
                )}
              </Col>
            </Row>
            <Row className="mt-4">
              <div className="d-flex align-items-center justify-content-end w-100 px-4">
                <div>
                  <Button
                    className="rounded-pill"
                    onClick={onSubmitClick}
                    label={intl.formatMessage({
                      id: "general.submit",
                      defaultMessage: "Submit",
                    })}
                  />
                </div>
              </div>
            </Row>
          </Card.Body>
        ) : (
          <Card.Body>
            <Row>
              <Col>
                <div className="mb-4">
                  <h6>
                    <FormattedMessage id="new-request.index.organisation" defaultMessage="Organisation" />
                  </h6>
                </div>
                <Organisational state={state} />
              </Col>
              <Col>
                <div className="mb-4">
                  <h6>
                    <FormattedMessage id="quote-request-detail.schedule" defaultMessage="Schedule" />
                  </h6>
                </div>
                <Schedule offPlatformOptionalFields />
              </Col>
              <Col>
                <Row className="mb-2">
                  <Col>
                    <div className="mb-4">
                      <h6>
                        <FormattedMessage id="general.packingList" defaultMessage="Packing list" />
                      </h6>
                    </div>
                  </Col>
                  {/* <Col> */}
                  {/* TODO: Need to add the functionality for this button */}
                  {/* <Button
                      variant=""
                      className="btn-grey h-26"
                      label={intl.formatMessage({
                        id: "quote-request-detail.generateCommercialInvoice",
                        defaultMessage: "Generate Commercial Invoice",
                      })}
                    /> */}
                  {/* </Col> */}
                </Row>
                {getValues(`packing_list.${0}.handling_unit`) && <PackingList state={state} />}
              </Col>
            </Row>
            <Row className="mt-5">
              <Col lg={4} md={6}>
                <div className="mb-4">
                  <h6>
                    <FormattedMessage id="quote-request-detail.service" defaultMessage="Service" />
                  </h6>
                </div>
                <Service state={state} isQuoteRequest={isQuoteRequest} />
              </Col>
              {isQuoteRequest && (
                <Col lg={4} md={6}>
                  <div className="mb-4">
                    <h6>
                      <FormattedMessage id="general.freightMode" defaultMessage="Freight mode" />
                    </h6>
                  </div>
                  <FreightMode {...props} />
                </Col>
              )}
              <Col lg={4} md={6}>
                <div className="mb-4">
                  <h6>
                    <FormattedMessage id="quick-request-detail.attachments" defaultMessage="Attachment" />
                  </h6>
                </div>
                <Attachment />
              </Col>
              <Col lg={4} md={6}>
                <div className="mb-4">
                  <h6>
                    <FormattedMessage id="general.additionalComments" defaultMessage="Additional comments" />
                  </h6>
                  <div>
                    <Textarea
                      name={isQuoteRequest ? "shipment.comments" : "quick_request.comments"}
                      placeholder={intl.formatMessage({
                        id: "general.typeHere",
                        defaultMessage: "Type here...",
                      })}
                      rows={3}
                      className="text-body bg-white mt-3"
                    />
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="mt-4">
              <div className="d-flex align-items-center justify-content-between w-100 px-4">
                <div>{/* TODO: add save as template and ask to save before quit here */}</div>
                <div>{renderSubmitButton()}</div>
              </div>
            </Row>
          </Card.Body>
        )}
      </Accordion.Collapse>
    </Card>
  );
}

FormConfirmation.defaultProps = {
  offPlatformOptionalFields: false,
  isNewShipment: false,
};

export default FormConfirmation;
